import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HTTPRequestStatus, RegisterPage } from "../../utils/models";
import { PasswordFormValues, SignInRequestErrorModel, SignUpRequest, SignUpRequestErrorModel, SignUpRequestErrors, SignUpRequestFields } from '../../model/auth';
import { User } from '../../model/user';

const errorModel = {
  email: '',
  username: '',
  first_name: '',
  last_name: '',
  social_security: '',
  password: '',
  password_confirmation: '',
  captcha_value: '',
} as SignUpRequestFields;

export interface AuthState {
  isAuthenticated: boolean;
  passwordValues: PasswordFormValues,
  error: SignInRequestErrorModel | SignUpRequestErrorModel | string | null;
  isLoading: boolean;
  forgotPasswordStatus: HTTPRequestStatus;
  forgotPasswordMessage: string;
  formErrors: SignUpRequestFields;
  httpError: string;
}

export const initialState: AuthState = {
  isAuthenticated: false,
  error: null,
  isLoading: false,
  passwordValues: {
    password: '',
    password_confirmation: '',
  },
  forgotPasswordStatus: HTTPRequestStatus.Idle,
  forgotPasswordMessage: '',
  formErrors: errorModel,
  httpError: '',
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signin: (state, action: PayloadAction<{ username: string, password: string }>) => {
      state.isLoading = true;
      state.error = null;
    },
    signinSuccess: (state) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.error = null;
    },
    signinError: (state, action: PayloadAction<AuthState["error"]>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.error = action.payload;
    },
    startLoading: (state) => {
      state.isLoading = true;
    },
    signup: (state, action: PayloadAction<Omit<SignUpRequest, keyof User>>) => {
      state.isLoading = true;
    },
    signupSuccess: (state) => {
      state.isLoading = false;
    },
    signupError: (state, action: PayloadAction<AuthState["error"]>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    precogValidateRegistrationField: (state, action: PayloadAction<{request: SignUpRequest, fields: string[], next: string}>) => {
      state.isLoading = true;
    },
    precogAutoValidateRegistrationField: (state, action: PayloadAction<{fields: SignUpRequestFields, next: string}>) => {},
    updatePasswordValues: (state, action: PayloadAction<{[key in keyof PasswordFormValues]?: string}>) => {
      state.passwordValues = {
        ...state.passwordValues,
        ...action.payload,
      }
    },
    setFormErrors: (state, action: PayloadAction<SignUpRequestFields>) => {
      state.formErrors = action.payload;
    },
    resetFormErrors: (state) => {
      state.formErrors = errorModel;
      state.httpError = '';
    },
    setHttpError: (state, action: PayloadAction<string>) => {
      state.httpError = action.payload;
    },
    finishLoading: (state) => {
      state.isLoading = false;
    },
    logout: (state) => {
      state.isLoading = true;
    },
    logoutSuccess: (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
    },
    logoutError: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    resetError: (state) => {
      state.error = null;
    },
    forgotPasswordRequestSuccess: (state, action) => {
      state.forgotPasswordStatus = HTTPRequestStatus.Success;
      state.forgotPasswordMessage = action.payload;
    },
    forgotPasswordRequestError: (state, action) => {
      state.forgotPasswordStatus = HTTPRequestStatus.Error;
      state.forgotPasswordMessage = action.payload;
    },
    resetForgotPasswordRequest: (state) => {
      state.forgotPasswordMessage = '';
      state.forgotPasswordStatus = HTTPRequestStatus.Idle;
    },
    makeResetPasswordRequest: (
      state,
      action: PayloadAction<{ email: string, password: string, passwordConfirmation: string, token: string }>
    ) => {
      state.error = null;
      state.isLoading = true;
    },
    makeResetPasswordRequestSuccess: (state) => {
      state.isLoading = false;
    },
    makeResetPasswordRequestFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export default authSlice.reducer;
